<template>
  <modal
    :show.sync="showModal"
    modal-classes="modal-dialog-centered modal-lg "
    @close="closeModal(false)"
    no-close-on-backdrop
  >
    <div class="">
      <div class="review modal-availability">
        <div class="text-center border-bottom mb-2">
          <img
            src="~rhomtrip_assets/src/img/icons/alerts/warning.png"
            alt="warning"
            class="icon-modal-alert"
          />
          <h2 class="modal-title">Change Confirmation Required</h2>
          <p v-if="hasPriceDifference">
            This change needs supplier confirmation and affects the total
            price. Review the price difference below and complete payment to
            confirm.
          </p>
          <p v-else>
            This change needs to be confirmed by the supplier before it's
            applied. No additional payment is required.
          </p>
        </div>

        <div class="card mb-3">
          <div class="col-12">
            <div class="row">
              <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                <h4 class="subtitle mb-1 mt-1">Original Details</h4>
                <div
                  class="item-list"
                  v-for="row in visibleRows"
                  :key="`original-${row.key}`"
                >
                  <span class="data-cancel">
                    <strong>{{ row.label }}</strong>{{ row.originalValue }}
                  </span>
                </div>
              </div>
              <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                <h4 class="subtitle mb-1 mt-1">Requested Changes</h4>
                <div
                  class="item-list"
                  v-for="row in visibleRows"
                  :key="`requested-${row.key}`"
                >
                  <span>
                    <strong>{{ row.label }}</strong>{{ row.requestedValue }}
                    <div class="badge badge-changed">Changed</div>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <base-alert type="info">
          <h4 class="subtitle mb-1 mt-1">How This Works</h4>

          <ol class="number-list">
            <li>
              <span
                >We'll request availability confirmation from the supplier for
                your changes.</span
              >
            </li>
            <li>
              <span
                >Your original reservation stays active as a backup until the
                supplier responds.</span
              >
            </li>
            <li>
              <span
                >Once the supplier confirms, we'll update your reservation
                automatically.</span
              >
            </li>
          </ol>
        </base-alert>

        <base-alert
          v-if="hasPriceDifference"
          type="warning"
          class="mb-0 info-min"
        >
          <i class="flaticon-warning mr-1"></i>
          <strong>Important Notes!</strong>

          <ul class="list">
            <li class="mb-0">
              Payment of the price difference is required to confirm this
              change.
            </li>
            <li class="mb-0">
              The reservation is subject to reconfirmation of the changes.
            </li>
            <li class="mb-0">
              A payment request for the price difference will be generated
              after this change is confirmed.
            </li>
          </ul>
        </base-alert>
      </div>
    </div>
    <template slot="footer">
      <base-button
        type="outline-neutral"
        class="min-button mb-0"
        @click="closeModal(false)"
        >Cancel</base-button
      >

      <base-button
        type="primary"
        class="min-button ml-auto mb-0"
        :disabled="!hasChanges"
        @click="submit()"
      >
        Confirm Changes
      </base-button>
    </template>
  </modal>
</template>

<script>
const COMPARISON_FIELDS = [
  { key: "from", label: "From" },
  { key: "to", label: "To:" },
  { key: "dateTime", label: "Date & Time:" },
  { key: "paxCount", label: "PAX N°:" },
  { key: "flightNumber", label: "Flight Number:" },
  { key: "railLine", label: "Rail Line:" },
  { key: "cruiseShip", label: "Cruise Ship:" },
  { key: "hourly", label: "Hourly:" },
  { key: "serviceType", label: "Service Type:" },
  { key: "vehicle", label: "Vehicle:" },
  { key: "paxName", label: "PAX:" },
  { key: "email", label: "Email:" },
  { key: "contactNumber", label: "Contact Number:" },
  { key: "pickupSign", label: "Pickup Sign:" },
  { key: "specialRequirements", label: "Special Requirements:" },
  { key: "childSeatsCount", label: "Child Seats N°:" },
  { key: "bookingReference", label: "Reservation Reference:" },
  { key: "totalCharges", label: "Total Charges:", currency: true },
];

export default {
  name: "ConfirmChanges",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    originalDetails: {
      type: Object,
      required: true,
    },
    requestedDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showModal: this.show,
    };
  },
  computed: {
    comparisonRows() {
      return COMPARISON_FIELDS.map((field) => {
        const originalValue = this.originalDetails[field.key];
        const requestedValue = this.requestedDetails[field.key];
        return {
          ...field,
          originalValue: this.formatValue(originalValue, field),
          requestedValue: this.formatValue(requestedValue, field),
          changed: originalValue !== requestedValue,
        };
      });
    },
    visibleRows() {
      return this.comparisonRows.filter((row) => row.changed);
    },
    hasChanges() {
      return this.comparisonRows.some((row) => row.changed);
    },
    hasPriceDifference() {
      const original = this.originalDetails.totalCharges;
      const requested = this.requestedDetails.totalCharges;
      return (
        typeof original === "number" &&
        typeof requested === "number" &&
        original.toFixed(2) !== requested.toFixed(2)
      );
    },
  },
  watch: {
    show(current) {
      this.showModal = current;
    },
  },
  methods: {
    formatValue(value, field) {
      if (field.currency && typeof value === "number") {
        return `$${value.toFixed(2)}`;
      }
      return value;
    },
    closeModal(status) {
      this.showModal = status;
      this.$emit("close", status);
    },
    submit() {
      this.$emit("confirm-changes", {
        original: this.originalDetails,
        requested: this.requestedDetails,
        changedFields: this.comparisonRows
          .filter((row) => row.changed)
          .map((row) => row.key),
      });
      this.closeModal(false);
    },
  },
};
</script>
